import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Input,
  Badge,
  Alert,
} from "reactstrap";
import Select2 from "react-select2-wrapper";
import "select2/dist/css/select2.min.css";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import helpers from "utils/helpers";
import userServices from "services/userServices";

const IpStatusReasonModal = ({
  isOpen,
  toggle,
  selectedRow,
  onConfirm,
}) => {
  const { t, language } = useTranslation();
  const { notify } = useNotification();
  const modalBodyRef = useRef(null);

  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);

  const isPermanent = selectedRow?.blockType === "PERMANENT";

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setComments("");
    }
  }, [isOpen]);

  const options = helpers.getIpUnblockReasonOptions(t);

  const handleSubmit = async () => {
    if (!selectedRow?.ip_address) {
      return notify("error", t("page.blockedIps.validation.invalidIp", "Invalid IP address"));
    }

    if (!reason) {
      return notify("error", t("page.blockedIps.validation.reasonRequired", "Please select a reason for unblocking."));
    }

    if (reason === "other" && !comments.trim()) {
      return notify("error", t("page.blockedIps.validation.commentsRequiredForOther", "Please specify a justification in the comments."));
    }

    setLoading(true);

    const selectedOption = options.find((opt) => opt.id === reason);
    const labelText = selectedOption?.text || reason;

    let finalReason = labelText;
    if (comments.trim()) {
      finalReason = `${labelText} - ${comments.trim()}`;
    }

    try {
      if (isPermanent) {
        const res = await userServices.unblockPermanentIp(selectedRow.ip_address);
        if (res?.success) {
          notify("success", t("page.blockedIps.permanentUnblockSuccess", "Permanent block removed successfully"));
          setReason("");
          setComments("");
          toggle();
          if (onConfirm) onConfirm({ ipAddress: selectedRow.ip_address, isActive: false });
        } else {
          notify("error", t(res?.error || "common.failed", res?.message || res?.error || "Failed to unblock IP"));
        }
      } else {
        if (onConfirm) {
          await onConfirm({
            ipAddress: selectedRow.ip_address,
            isActive: false,
            reason: finalReason,
          });
        }

        setReason("");
        setComments("");
        toggle();
      }
    } catch (err) {
      console.error("Unblock failed:", err);
      notify("error", t("common.failed", "Failed to unblock IP"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="md"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={handleClose} className="border">
        {t("page.blockedIps.modal.unblockTitle", "Unblock IP Address")}
      </ModalHeader>

      <ModalBody ref={modalBodyRef}>
        <div className="mb-3 d-flex align-items-center justify-content-between">
          <div>
            <span className="text-muted text-sm">{t("page.blockedIps.modal.ipAddress", "IP Address")}:</span>{" "}
            <span className="font-weight-600 ml-1">{selectedRow?.ip_address}</span>
          </div>
          <div>
            <Badge
              className={`badge-pill px-2.5 py-0.5 text-uppercase mr-1 ${isPermanent ? "badge-status-permanent" : "badge-status-temporary"}`}
            >
              {isPermanent
                ? t("page.blockedIps.status.permanent", "Permanent")
                : t("page.blockedIps.status.temporary", "Temporary")}
            </Badge>
            <Badge
              className={`badge-pill px-2.5 py-0.5 text-uppercase ${selectedRow?.source === "ADMIN" ? "badge-source-admin" : "badge-source-system"}`}
            >
              {selectedRow?.source === "ADMIN"
                ? t("page.blockedIps.sources.admin", "Admin")
                : t("page.blockedIps.sources.system", "System")}
            </Badge>
          </div>
        </div>

        {isPermanent && (
          <Alert color="warning" className="text-sm mb-3">
            <strong>{t("common.notice", "Notice")}:</strong>{" "}
            {t(
              "page.blockedIps.permanentUnblockNotice",
              "This IP has a permanent block. Confirming will permanently remove this restriction."
            )}
          </Alert>
        )}

        <div className="mb-3">
          <label className="form-control-label text-sm font-weight-600 mb-1">
            {t("page.blockedIps.modal.unblockReason", "Reason for Unblocking")} <span className="text-danger">*</span>
          </label>
          <Select2
            key={`unblock-reason-${language}`}
            className="form-control"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={{
              width: "100%",
              dropdownParent: modalBodyRef.current,
              data: options,
            }}
          />
        </div>

        <div className="mb-2">
          <label className="form-control-label text-sm font-weight-600 mb-1">
            {t("page.blockedIps.modal.additionalComments", "Additional Comments / Notes")}
            {reason === "other" && <span className="text-danger"> *</span>}
          </label>
          <Input
            type="textarea"
            rows={3}
            className="form-control"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={t("page.blockedIps.modal.unblockCommentsPlaceholder", "Enter justification or reference details...")}
            disabled={loading}
          />
        </div>
      </ModalBody>

      <ModalFooter className="border">
        <Button
          color="secondary"
          onClick={handleClose}
          disabled={loading}
        >
          {t("common.cancel", "Cancel")}
        </Button>

        <Button
          color="success"
          onClick={handleSubmit}
          disabled={loading || !reason}
        >
          {loading && <Spinner size="sm" className="mr-2" />}
          {t("page.blockedIps.actions.unblock", "Unblock IP")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default IpStatusReasonModal;
